import { useRequestURL } from '#imports'
import type { RouteLocationNormalizedLoaded } from 'vue-router'

import type { Bcp47Locale, Market } from '@backmarket/http-api'
import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'

const COUNTRIES_WITH_ENGLISH_LOCALE = [
  MarketCountryCode.IE,
  MarketCountryCode.GB,
  MarketCountryCode.AU,
  MarketCountryCode.US,
]

const englishLocale: { [key: string]: string } = {
  [MarketCountryCode.IE]: 'en-GB',
  [MarketCountryCode.GB]: 'en-GB',
  [MarketCountryCode.AU]: 'en-GB',
  [MarketCountryCode.US]: 'en-US',
}

const buildBmRedirectHome = (props: {
  currentRoute: RouteLocationNormalizedLoaded
  originUrl: string
  market: Market
}) => {
  const locale = props.currentRoute.params.locale as string

  const relativeUrl = `/${locale}/`

  return new URL(relativeUrl, props.originUrl).href
}

function buildSheerIdUrl({
  sheerIdUrl,
  market,
  bmUserId,
  currentRoute,
  originUrl,
  locale,
}: {
  sheerIdUrl: string
  market: Market
  bmUserId: string
  currentRoute: RouteLocationNormalizedLoaded
  originUrl: string
  locale: Bcp47Locale
}) {
  const url = new URL(sheerIdUrl)
  url.searchParams.append('country', market.countryCode)

  // SheerID expects a lang as a locale... except for UK, Ireland, Australia and US where it expects a locale with this format: 'en-GB'
  const lang = locale.split('-')[0]

  url.searchParams.append(
    'locale',
    COUNTRIES_WITH_ENGLISH_LOCALE.includes(market.countryCode)
      ? englishLocale[market.countryCode]
      : lang,
  )

  // note: sheerID is expecting a 'redirectURL' query param (not 'redirectUrl')
  url.searchParams.append(
    'redirectURL',
    buildBmRedirectHome({ currentRoute, market, originUrl }),
  )
  url.searchParams.append('userid', bmUserId)

  return url.href
}

export function buildEducatorVerificationUrl({
  currentRoute,
  market,
  bmUserId = 'USER_ID',
  sheerIdUrl,
  authenticated,
  originUrl,
  locale,
}: {
  currentRoute: RouteLocationNormalizedLoaded
  market: Market
  bmUserId?: string
  sheerIdUrl: string
  authenticated: boolean
  originUrl: string
  locale: Bcp47Locale
}) {
  const baseUrl = useRequestURL().origin

  const educatorVerificationUrl = buildSheerIdUrl({
    currentRoute,
    market,
    bmUserId,
    sheerIdUrl,
    originUrl,
    locale,
  })

  if (authenticated) return educatorVerificationUrl

  try {
    const redirectionUrl = new URL(`${baseUrl}/${locale}/auth/login`)
    redirectionUrl.searchParams.append('next', educatorVerificationUrl)
    redirectionUrl.searchParams.append('type', 'external')
    redirectionUrl.searchParams.append('bm_journey', 'educator-discount')

    return redirectionUrl.href
  } catch (err) {
    const invalidUrlError = new Error(
      `"${baseUrl}/${locale}/auth/login" is an invalid URL.`,
    )
    if (err instanceof Error) invalidUrlError.stack = err.stack
    throw invalidUrlError
  }
}
